import { INSTALLMENTS_COUNT_LIST_FOR_DEFAULT_PROMO } from 'src/constants/learn-more';
import { allowedCurrencies, CurrencyType } from 'src/types/currency';

import { deriveProductFacts } from './product-facts';
import { type WidgetDecisionInput } from './types';

export type WebviewContentDecision =
  | 'long-term'
  | 'pay-next-month-only'
  | 'non-standard'
  | 'schedule'
  | 'calculator';

export type WebviewDecision = Readonly<{
  surface: 'webview';
  content: WebviewContentDecision;
}>;

export const selectWebviewContent = ({
  shouldOpenLongTermLearnMore,
  isNonDefaultWidget,
  isPixWidget,
}: {
  shouldOpenLongTermLearnMore: boolean;
  isNonDefaultWidget: boolean;
  isPixWidget: boolean;
}): WebviewContentDecision => {
  if (shouldOpenLongTermLearnMore) return 'long-term';
  if (isNonDefaultWidget) return 'non-standard';
  if (isPixWidget) return 'schedule';

  return 'calculator';
};

/** Select the generic webview composition from the shared product facts. */
export const decideWebview = (input: WidgetDecisionInput): WebviewDecision => {
  const facts = deriveProductFacts(input);

  if (input.offers.monthlyBilling && !facts.hasInstallments) {
    return {
      surface: 'webview',
      content: 'pay-next-month-only',
    };
  }

  const isValidCountry =
    input.formattedCurrency !== undefined &&
    allowedCurrencies.includes(input.formattedCurrency);
  // This mirrors the existing `length !== 0` check, including its treatment of
  // an absent installments field. Canonical adapted offers always provide it.
  const hasConfiguredPlans = input.offers.installments?.length !== 0;
  const isPixWidget =
    isValidCountry &&
    input.formattedCurrency !== CurrencyType.AED &&
    !hasConfiguredPlans &&
    INSTALLMENTS_COUNT_LIST_FOR_DEFAULT_PROMO.includes(input.installmentsCount);
  const isNonDefaultWidget = !isPixWidget && !facts.shouldOpenLongTermLearnMore;
  const content = selectWebviewContent({
    shouldOpenLongTermLearnMore: facts.shouldOpenLongTermLearnMore,
    isNonDefaultWidget,
    isPixWidget,
  });

  return {
    surface: 'webview',
    content,
  };
};
